// src/services/bingo_api/hooks.ts
import { APIResponseType } from "../../../types/generics/APIResponse.type";
import {
  createGetApiHook,
  createPostApiHook,
  createPutApiHook,
} from "../../api/apiHooks";
import { CreateBingoBody, BingoSessionResponse } from "./bingo_types";

type Id = string | number;

/** ---------- Types (adjust to your backend responses if you have exact shapes) ---------- */
export interface BingoSession {
  id: string | number;
  bookingId: string | number;
  sessionId: string | number;
  status: "created" | "started" | "ended";
  createdAt?: string;
  [k: string]: any;
}

export interface PlayerBoard {
  userId: string | number;
  sessionId: string | number| null;
  cells: Array<{ n: number; marked: boolean }>;
  [k: string]: any;
}

export interface JoinBingoBody {
  user_id: number;
  username: string;
}
export interface MarkBody {
  user_id: number | null;       // required by backend
  cell_number: number;   // required by backend
}

/** ---------- Endpoint builders (avoid  hard-coded {param} templates) ---------- */
const ep = {
  create: () => `bingo/sessions`,
  joinByBooking: ({ bookingId }: { bookingId: string | number }) =>
    `bingo/sessions/booking/${bookingId}/join`,
  leaveByBooking: ({ bookingId }: { bookingId: string | number }) =>
    `bingo/sessions/booking/${bookingId}/leave`,
  getByBooking: ({ bookingId }: { bookingId: string | number }) =>
    `bingo/sessions/bookings/${bookingId}`,
  getBySession: ({ sessionId }: { sessionId: string | number | null }) =>
    `bingo/sessions/${sessionId}`,
  draw: (sessionId: string | number | null) => `bingo/sessions/${sessionId}/draw`,
  mark: (sessionId: string | number | null) => `bingo/sessions/${sessionId}/mark`,
  start: (sessionId: string | number | null) => `bingo/sessions/${sessionId}/start`,
  reset: (sessionId: string | number | null) => `bingo/sessions/${sessionId}/reset`,
  end: (sessionId: string | number | null) => `bingo/sessions/${sessionId}/end`,
};

/** ---------- Hooks (use in components) ---------- */

/** POST /api/v1/bingo/sessions */
export const useCreateBingoSession = createPostApiHook<
  CreateBingoBody,
  {},
  APIResponseType<BingoSessionResponse>
>(ep.create(), { isFormDataMethod: false });

/** POST /api/v1/bingo/sessions/booking/{bookingId}/join */
export const useJoinBingoSession = createPostApiHook<
  JoinBingoBody,                                    // ← BODY
  { bookingId: string | number },                   // ← URL PARAMS
  APIResponseType<{ session: BingoSession }>
>(ep.joinByBooking, { isFormDataMethod: false });

/** POST /api/v1/bingo/sessions/booking/{bookingId}/leave */
export const useLeaveBingoSession = createPostApiHook<
  {},
  { bookingId: string | number },
  APIResponseType<{}>
>(ep.leaveByBooking, { isFormDataMethod: false });

/** GET /api/v1/bingo/sessions/booking/{bookingId} */
export const useGetBingoSessionByBookingId = createGetApiHook<
  APIResponseType<BingoSessionResponse>,
  { bookingId: string | number }
>(ep.getByBooking);

/** GET /api/v1/bingo/sessions/{sessionId} */
export const useGetBingoSessionBySessionId = createGetApiHook<
  APIResponseType<BingoSession>,
  { sessionId: string | number }
>(ep.getBySession);

/** GET /api/v1/bingo/sessions/{sessionId}/board/{userId} */


/** PUT /api/v1/bingo/sessions/{sessionId}/draw */
export const useDrawNextNumber = createPutApiHook<
  {},
  { sessionId: Id },
  APIResponseType<{ number: number }>
>(({ sessionId }) => `bingo/sessions/${sessionId}/draw`, {
  isFormDataMethod: false,
});

/** PUT /api/v1/bingo/sessions/{sessionId}/mark */
export const useMarkBingoCell = createPutApiHook<
  MarkBody,
  { sessionId: string | number | null },
  APIResponseType<PlayerBoard>
>(({ sessionId }) => `bingo/sessions/${sessionId}/mark`, {
  isFormDataMethod: false,
});
/** PUT /api/v1/bingo/sessions/{sessionId}/start */
export const useStartBingoSession = createPutApiHook<
  {},
  { sessionId: Id },
  APIResponseType<BingoSession>
>(({ sessionId }) => `bingo/sessions/${sessionId}/start`, {
  isFormDataMethod: false,
});
/** PUT /api/v1/bingo/sessions/{sessionId}/reset */
export const useResetBingoSession = createPutApiHook<
  {},
  { sessionId: Id },
  APIResponseType<BingoSession>
>(({ sessionId }) => `bingo/sessions/${sessionId}/reset`, {
  isFormDataMethod: false,
});

/** PUT /api/v1/bingo/sessions/{sessionId}/end */
export const useEndBingoSession = createPutApiHook<
  {},
  { sessionId: Id },
  APIResponseType<BingoSession>
>(({ sessionId }) => `bingo/sessions/${sessionId}/end`, {
  isFormDataMethod: false,
});
